/**
 * SEC.3 — Honeypot canary detection + browser fingerprint capture.
 *
 * When `apiFetchRaw` / `useApi` see a response body containing the marker
 * `{ __deepsync_canary: "gotcha" }`, this module:
 *
 *   1. Computes a high-entropy browser fingerprint (WebGL, canvas, audio,
 *      screen, locale, timezone, hardware, network).
 *   2. POSTs the fingerprint to `/api/honeypot/fingerprint` so the backend
 *      can cross-reference with the original decoy hit + broadcast a
 *      `critical` SyncError to admin users (Layer 2 capture).
 *   3. Dispatches a `window` CustomEvent `honeypot:gotcha` so the React
 *      `HoneypotGotchaModal` component can render the "Gotcha" overlay.
 *
 * Threat model coverage:
 *   - This module ONLY fires when the response is actually rendered in a
 *     browser executing our JS bundle (i.e., a DevTools-observer attacker
 *     using deep-sync.io). curl / Python scrapers never reach this layer;
 *     they're caught at Layer 1 (backend `honeypot.fired` log + admin email).
 *   - The fingerprint POST is best-effort — failures are silent so an
 *     attacker disabling JS or intercepting the POST doesn't prevent the
 *     server-side Layer 1 capture from already having fired.
 */

export const CANARY_MARKER_KEY = '__deepsync_canary' as const;
export const CANARY_MARKER_VALUE = 'gotcha' as const;
export const CANARY_EVENT = 'honeypot:gotcha' as const;

type Json = Record<string, unknown>;

interface CanaryEventDetail {
  decoyId: string;
  serverMeta: Json;
}

const _seenDecoyIds = new Set<string>();

/**
 * Peek into a response body to check for the canary marker. Clones the
 * response so the original is still readable by the caller. Returns the
 * decoy ID + server meta if the marker is present, otherwise null.
 *
 * Best-effort: any parse error returns null (legitimate non-JSON responses
 * must not throw).
 */
export async function detectCanaryMarker(response: Response): Promise<CanaryEventDetail | null> {
  try {
    if (!response.ok || response.status >= 400) return null;
    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return null;

    const cloned = response.clone();
    const body = await cloned.json() as Json;
    if (body[CANARY_MARKER_KEY] !== CANARY_MARKER_VALUE) return null;

    const meta = (body._meta as Json) || {};
    const decoyId = String(meta.decoy_id ?? 'unknown');
    return { decoyId, serverMeta: meta };
  } catch {
    return null;
  }
}

/**
 * Pipe the canary handling alongside an in-flight fetch — does NOT change
 * the response visible to the caller. Returns the same Response so the
 * call site can keep going as if nothing was tampered with.
 */
export async function pipeCanaryCheck(response: Response): Promise<Response> {
  const detail = await detectCanaryMarker(response);
  if (detail && !_seenDecoyIds.has(detail.decoyId)) {
    _seenDecoyIds.add(detail.decoyId);
    void fireCanary(detail);
  }
  return response;
}

async function fireCanary(detail: CanaryEventDetail): Promise<void> {
  try {
    const fingerprint = await computeBrowserFingerprint();
    void postFingerprint(detail.decoyId, fingerprint);
  } catch {
    // Fingerprint failure must not break the modal trigger.
  }
  try {
    window.dispatchEvent(new CustomEvent<CanaryEventDetail>(CANARY_EVENT, { detail }));
  } catch {
    // Non-DOM environments (SSR, tests) — no-op.
  }
}

async function postFingerprint(decoyId: string, fingerprint: Json): Promise<void> {
  try {
    const base = import.meta.env.VITE_API_URL || '/api';
    await fetch(`${base}/honeypot/fingerprint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decoy_id: decoyId, ...fingerprint }),
      keepalive: true,
    });
  } catch {
    // Silent fail — server-side Layer 1 already captured the original hit.
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Browser fingerprint collection — high entropy, defensive against missing
// APIs. Every field is best-effort; failure to compute one never breaks the
// whole fingerprint.
// ────────────────────────────────────────────────────────────────────────────

export async function computeBrowserFingerprint(): Promise<Json> {
  const nav = safeNavigator();
  const scr = safeScreen();
  const winFP = safeWindow();
  const intlFP = safeIntl();
  const webgl = safeWebGL();
  const canvas = await safeCanvas();
  const audio = await safeAudio();
  const conn = safeConnection();
  const storage = await safeStorage();

  return {
    captured_at_client: new Date().toISOString(),
    user_agent: navigator.userAgent,
    user_agent_data: nav.userAgentData,
    language: navigator.language,
    languages: Array.from(navigator.languages ?? []),
    platform: nav.platform,
    hardware_concurrency: nav.hardwareConcurrency,
    device_memory_gb: nav.deviceMemory,
    online: navigator.onLine,
    cookie_enabled: navigator.cookieEnabled,
    do_not_track: (navigator as Navigator & { doNotTrack?: string | null }).doNotTrack,
    pdf_viewer_enabled: (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled,
    plugins: nav.plugins,
    mime_types: nav.mimeTypes,

    screen_width: scr.width,
    screen_height: scr.height,
    screen_avail_width: scr.availWidth,
    screen_avail_height: scr.availHeight,
    screen_color_depth: scr.colorDepth,
    screen_pixel_depth: scr.pixelDepth,
    screen_orientation: scr.orientation,

    window_inner_width: winFP.innerWidth,
    window_inner_height: winFP.innerHeight,
    window_outer_width: winFP.outerWidth,
    window_outer_height: winFP.outerHeight,
    device_pixel_ratio: winFP.devicePixelRatio,

    timezone: intlFP.timezone,
    timezone_offset_minutes: intlFP.tzOffset,
    locale: intlFP.locale,
    calendars: intlFP.calendars,
    numbering_systems: intlFP.numberingSystems,

    webgl_renderer: webgl.renderer,
    webgl_vendor: webgl.vendor,
    webgl_version: webgl.version,
    webgl_shading_language_version: webgl.shadingLanguageVersion,
    webgl_extensions: webgl.extensions,

    canvas_fingerprint_hash: canvas.hash,
    canvas_dimensions: canvas.dims,

    audio_fingerprint_hash: audio.hash,

    connection_effective_type: conn.effectiveType,
    connection_downlink_mbps: conn.downlink,
    connection_rtt_ms: conn.rtt,
    connection_save_data: conn.saveData,
    connection_type: conn.type,

    storage_quota_bytes: storage.quota,
    storage_usage_bytes: storage.usage,

    referrer: document.referrer,
    page_url: window.location.href,
    page_path: window.location.pathname,
    history_length: window.history.length,

    touch_max_points: nav.maxTouchPoints,
    color_scheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    reduced_motion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
}

function safeNavigator() {
  try {
    const n = navigator as Navigator & {
      userAgentData?: unknown;
      deviceMemory?: number;
      platform?: string;
    };
    return {
      userAgentData: n.userAgentData ?? null,
      platform: n.platform ?? '',
      hardwareConcurrency: n.hardwareConcurrency ?? null,
      deviceMemory: n.deviceMemory ?? null,
      maxTouchPoints: n.maxTouchPoints ?? 0,
      plugins: Array.from(navigator.plugins ?? []).map((p) => ({
        name: p.name,
        description: p.description,
        filename: p.filename,
      })),
      mimeTypes: Array.from(navigator.mimeTypes ?? []).map((m) => ({
        type: m.type,
        description: m.description,
        suffixes: m.suffixes,
      })),
    };
  } catch {
    return { userAgentData: null, platform: '', hardwareConcurrency: null, deviceMemory: null, maxTouchPoints: 0, plugins: [], mimeTypes: [] };
  }
}

function safeScreen() {
  try {
    return {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      orientation: screen.orientation?.type ?? null,
    };
  } catch {
    return { width: 0, height: 0, availWidth: 0, availHeight: 0, colorDepth: 0, pixelDepth: 0, orientation: null };
  }
}

function safeWindow() {
  try {
    return {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight,
      devicePixelRatio: window.devicePixelRatio,
    };
  } catch {
    return { innerWidth: 0, innerHeight: 0, outerWidth: 0, outerHeight: 0, devicePixelRatio: 1 };
  }
}

function safeIntl() {
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    return {
      timezone: opts.timeZone ?? '',
      tzOffset: new Date().getTimezoneOffset(),
      locale: opts.locale ?? '',
      calendars: (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('calendar') ?? null,
      numberingSystems: (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('numberingSystem') ?? null,
    };
  } catch {
    return { timezone: '', tzOffset: 0, locale: '', calendars: null, numberingSystems: null };
  }
}

function safeWebGL() {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return { renderer: null, vendor: null, version: null, shadingLanguageVersion: null, extensions: [] };
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      renderer: dbg ? gl.getParameter((dbg as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      vendor: dbg ? gl.getParameter((dbg as { UNMASKED_VENDOR_WEBGL: number }).UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      extensions: gl.getSupportedExtensions() ?? [],
    };
  } catch {
    return { renderer: null, vendor: null, version: null, shadingLanguageVersion: null, extensions: [] };
  }
}

async function safeCanvas(): Promise<{ hash: string | null; dims: { w: number; h: number } | null }> {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 280;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { hash: null, dims: null };
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Cwm fjordbank glyphs vext quiz, 🍯', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Cwm fjordbank glyphs vext quiz, 🍯', 4, 17);
    const data = canvas.toDataURL();
    const hash = await sha256Hex(data);
    return { hash, dims: { w: 280, h: 60 } };
  } catch {
    return { hash: null, dims: null };
  }
}

async function safeAudio(): Promise<{ hash: string | null }> {
  try {
    const AC = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!AC) return { hash: null };
    const ctx = new (AC as typeof AudioContext)(undefined as unknown as AudioContextOptions);
    const osc = ctx.createOscillator();
    const analyser = ctx.createAnalyser();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = 1000;
    gain.gain.value = 0;
    osc.connect(analyser);
    analyser.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    const data = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatFrequencyData(data);
    osc.stop();
    void ctx.close();
    const summed = Array.from(data.slice(0, 256)).map((v) => Math.round(v * 100) / 100).join(',');
    const hash = await sha256Hex(summed);
    return { hash };
  } catch {
    return { hash: null };
  }
}

function safeConnection() {
  try {
    const c = (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean; type?: string } }).connection;
    if (!c) return { effectiveType: null, downlink: null, rtt: null, saveData: null, type: null };
    return {
      effectiveType: c.effectiveType ?? null,
      downlink: c.downlink ?? null,
      rtt: c.rtt ?? null,
      saveData: c.saveData ?? null,
      type: c.type ?? null,
    };
  } catch {
    return { effectiveType: null, downlink: null, rtt: null, saveData: null, type: null };
  }
}

async function safeStorage(): Promise<{ quota: number | null; usage: number | null }> {
  try {
    if (!navigator.storage?.estimate) return { quota: null, usage: null };
    const est = await navigator.storage.estimate();
    return { quota: est.quota ?? null, usage: est.usage ?? null };
  } catch {
    return { quota: null, usage: null };
  }
}

async function sha256Hex(input: string): Promise<string> {
  if (!crypto?.subtle) return `len:${input.length}`;
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
